import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { createSecureLinkClient } from '@roc-capital/secure-link';
import { SecureLinkSessionStatus, useSecureLinkSession } from '@roc-capital/secure-link/react';
import { Layout, LoginLogoImg, useBaseStore } from '@roc/feature-app-core';
import { Button } from '@roc/ui';
import { ScopeOfWorkV2Form } from '@roc/feature-sow-v2';
import { useStore } from '../../hooks';

export const Message = ({ title, text, action }: { title: string; text: string; action?: { label: string; onClick: () => void } }) => (
  <Box textAlign="center" py={6} px={2} maxWidth={520} mx="auto">
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography color="textSecondary">{text}</Typography>
    {action && (
      <Box mt={3}>
        <Button testId="retry" variant="outlined" color="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      </Box>
    )}
  </Box>
);

export const MessagePage = ({ children }: { children: React.ReactNode }) => (
  <Layout maxWidth={'lg'}>
    <Box style={{ display: 'flex', justifyContent: 'center' }}>
      <LoginLogoImg imgStyle={{ width: '150px' }} />
    </Box>
    {children}
  </Layout>
);

/** What the scope of work secure link pages show while their session isn't active: loading, ended or unavailable. */
export const SecureLinkSessionStatusPage = ({ status, retry }: { status: SecureLinkSessionStatus; retry: () => void }) => (
  <MessagePage>
    {status === 'loading' && (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )}
    {status === 'ended' && (
      <Message
        title="Your session has ended"
        text="For your security, sessions end after 15 minutes or when the link is opened in another window. Open the link from your email again to continue."
      />
    )}
    {status === 'unavailable' && (
      <Message
        title="We couldn't open your scope of work"
        text="This is usually temporary. Check your connection and try again."
        action={{ label: 'Try again', onClick: retry }}
      />
    )}
  </MessagePage>
);

/**
 * The scope of work revision for secure links: /link/scope-of-work-revision. The contractor arrives here from the
 * platform landing page (/platform-app/link#token), which started the session; every call is authorized by that
 * session, and the revision draw comes from it on the server. Replaces /scope-of-work-revision/:encodedParams, which
 * carried the draw id and a username in base64.
 */
export const SecureLinkScopeOfWorkRevisionPage = observer(() => {
  const { environmentStore } = useBaseStore();
  const client = useMemo(
    () => createSecureLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
  const { status, request, retry } = useSecureLinkSession(client, { purpose: 'SOW_REVISION' });

  const { scopeOfWorkRevisionStore } = useStore();
  const { scopeOfWorkV2FormStore } = scopeOfWorkRevisionStore;

  // A layout effect, so it runs before the revision's first data fetch on the same commit.
  useLayoutEffect(() => scopeOfWorkRevisionStore.setRequest(request), [scopeOfWorkRevisionStore, request]);

  useEffect(() => {
    if (status !== 'active') return;
    scopeOfWorkRevisionStore.fetchSOWRevision();
    return () => {
      scopeOfWorkRevisionStore.reset();
    };
  }, [status]);

  if (status === 'active') {
    return <ScopeOfWorkV2Form store={scopeOfWorkV2FormStore} />;
  }

  return <SecureLinkSessionStatusPage status={status} retry={retry} />;
});

/**
 * The old scope of work link formats, which carried their ids and a username in base64: /scope-of-work-revision/
 * (draw id), /scope-of-work-v2/ (scope of work and lender ids) and /create-scope-of-work/ (lender id), each with
 * :encodedParams. Those links no longer work: the lender sends a new one, which opens /link/scope-of-work-revision,
 * /link/scope-of-work or /link/create-scope-of-work.
 */
export const ScopeOfWorkLinkReplacedPage = () => (
  <MessagePage>
    <Message
      title="This link is no longer valid"
      text="Please ask your lender to send you a new link."
    />
  </MessagePage>
);

export default SecureLinkScopeOfWorkRevisionPage;
